import { useEffect, useMemo, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { useProfile, candidateSections, type Education } from '@/hooks/use-profile'
import { Button } from '@/components/ui/button'
import { MonthYearRangeFields } from '@/components/ui/month-year-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkillChipField } from '@/components/skills/skill-chip-field'
import {
  compareMonthYear,
  isCompleteMonthYear,
  isPartialMonthYear,
  monthYearDraftKey,
  monthYearToIso,
  parseIsoToMonthYear,
  type MonthYearDraft,
} from '@/lib/month-year'
import { appendSkillName, sortedSkillNamesKey } from '@/utils/profile-skill-suggestions'
import { Textarea } from '@/components/ui/textarea'
import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
} from '@/components/artifact/artifact-canvas-layout'

interface EducationCanvasProps {
  education: Education | null
  onClose: () => void
}

interface EducationDraft {
  institution: string
  degree: string
  fieldOfStudy: string
  start?: MonthYearDraft
  end?: MonthYearDraft
  currentlyStudying: boolean
  description: string
  skills: string[]
}

function buildDraft(education: Education | null): EducationDraft {
  return {
    institution: education?.institution ?? '',
    degree: education?.degree ?? '',
    fieldOfStudy: education?.field_of_study ?? '',
    start: parseIsoToMonthYear(education?.start_date),
    end: parseIsoToMonthYear(education?.end_date),
    currentlyStudying: education ? !education.end_date : false,
    description: education?.description ?? '',
    skills: [...(education?.related_skills ?? [])],
  }
}

export function EducationCanvas({ education, onClose }: EducationCanvasProps) {
  const {
    profile,
    addEducationAsync,
    updateEducationAsync,
    deleteEducationAsync,
  } = useProfile()
  const candidate = candidateSections(profile)

  const isEditMode = education !== null && !!education.id

  const liveEducation = useMemo<Education | null>(() => {
    if (!isEditMode || !education?.id) return null
    const match = (candidate.education ?? []).find((e) => e.id === education.id)
    return match ?? education
  }, [isEditMode, education, candidate.education])

  const initialDraft = useMemo(
    () => buildDraft(liveEducation ?? education),
    [liveEducation, education]
  )
  const [draft, setDraft] = useState<EducationDraft>(initialDraft)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    setDraft(buildDraft(liveEducation ?? education))
    setValidationError(null)
    setSubmitError(null)
    setConfirmingDelete(false)
  }, [liveEducation, education])

  const fieldsDirty =
    draft.institution !== initialDraft.institution ||
    draft.degree !== initialDraft.degree ||
    draft.fieldOfStudy !== initialDraft.fieldOfStudy ||
    draft.description !== initialDraft.description ||
    monthYearDraftKey(draft.start) !== monthYearDraftKey(initialDraft.start) ||
    monthYearDraftKey(draft.end) !== monthYearDraftKey(initialDraft.end) ||
    draft.currentlyStudying !== initialDraft.currentlyStudying

  const skillsDirty = sortedSkillNamesKey(draft.skills) !== sortedSkillNamesKey(initialDraft.skills)
  const isDirty = isEditMode ? fieldsDirty || skillsDirty : true

  const attemptAddRelatedSkill = (rawName: string) => {
    const result = appendSkillName(draft.skills, rawName)
    if (!result.ok) {
      setValidationError(result.error)
      return
    }
    setDraft((current) => ({ ...current, skills: result.skills }))
    setValidationError(null)
  }

  const removeSkill = (skill: string) => {
    setDraft((current) => ({
      ...current,
      skills: current.skills.filter((value) => value !== skill),
    }))
    setValidationError(null)
  }

  const handleSave = async () => {
    setValidationError(null)
    setSubmitError(null)

    if (!draft.institution.trim()) {
      setValidationError('Institution is required.')
      return
    }
    if (!draft.degree.trim()) {
      setValidationError('Degree is required.')
      return
    }
    if (isPartialMonthYear(draft.start)) {
      setValidationError('Enter both start month and year, or leave start empty.')
      return
    }
    if (!draft.currentlyStudying && !isCompleteMonthYear(draft.end)) {
      setValidationError(
        'End month and year are required, or mark that you currently study here.',
      )
      return
    }
    if (
      !draft.currentlyStudying &&
      isCompleteMonthYear(draft.start) &&
      isCompleteMonthYear(draft.end) &&
      compareMonthYear(draft.end, draft.start) < 0
    ) {
      setValidationError('End date cannot be before start date.')
      return
    }

    const startIso = isCompleteMonthYear(draft.start) ? monthYearToIso(draft.start) : null
    const endIso =
      draft.currentlyStudying || !isCompleteMonthYear(draft.end)
        ? null
        : monthYearToIso(draft.end)

    setIsSaving(true)
    try {
      if (!isEditMode) {
        await addEducationAsync({
          institution: draft.institution.trim(),
          degree: draft.degree.trim(),
          field_of_study: draft.fieldOfStudy.trim(),
          start_date: startIso,
          end_date: endIso,
          description: draft.description.trim(),
          related_skills: draft.skills,
        })
      } else if (liveEducation?.id) {
        const updates: Partial<Omit<Education, 'id'>> = {}
        if (fieldsDirty) {
          updates.institution = draft.institution.trim()
          updates.degree = draft.degree.trim()
          updates.field_of_study = draft.fieldOfStudy.trim()
          updates.start_date = startIso
          updates.end_date = endIso
          updates.description = draft.description.trim()
        }
        if (skillsDirty) {
          updates.related_skills = draft.skills
        }

        if (Object.keys(updates).length > 0) {
          await updateEducationAsync({
            id: liveEducation.id,
            updates,
          })
        }
      }

      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save education.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!isEditMode || !liveEducation?.id) return

    if (!confirmingDelete) {
      setConfirmingDelete(true)
      return
    }

    setSubmitError(null)
    setIsDeleting(true)
    try {
      await deleteEducationAsync(liveEducation.id)
      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to delete education.')
      setIsDeleting(false)
      setConfirmingDelete(false)
    }
  }

  const isBusy = isSaving || isDeleting

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <form
        className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="artifact-education-institution">Institution *</Label>
            <Input
              id="artifact-education-institution"
              value={draft.institution}
              onChange={(event) => {
                setDraft((current) => ({ ...current, institution: event.target.value }))
                setValidationError(null)
              }}
              placeholder="University or provider"
              disabled={isBusy}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="artifact-education-degree">Degree *</Label>
            <Input
              id="artifact-education-degree"
              value={draft.degree}
              onChange={(event) => {
                setDraft((current) => ({ ...current, degree: event.target.value }))
                setValidationError(null)
              }}
              placeholder="Degree or qualification"
              disabled={isBusy}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-education-field">Field of study</Label>
          <Input
            id="artifact-education-field"
            value={draft.fieldOfStudy}
            onChange={(event) => {
              setDraft((current) => ({ ...current, fieldOfStudy: event.target.value }))
            }}
            placeholder="Computer Science"
            disabled={isBusy}
          />
        </div>

        <MonthYearRangeFields
          idPrefix="artifact-education"
          ongoingLabel="I currently study here"
          ongoingChecked={draft.currentlyStudying}
          onOngoingChange={(checked) => {
            setDraft((current) => ({
              ...current,
              currentlyStudying: checked,
              end: checked ? undefined : current.end,
            }))
            setValidationError(null)
          }}
          start={draft.start}
          end={draft.end}
          onStartChange={(start) => {
            setDraft((current) => ({ ...current, start }))
            setValidationError(null)
          }}
          onEndChange={(end) => {
            setDraft((current) => ({ ...current, end }))
            setValidationError(null)
          }}
          disabled={isBusy}
        />

        <div className="space-y-2">
          <Label htmlFor="artifact-education-description">Description</Label>
          <Textarea
            id="artifact-education-description"
            rows={4}
            value={draft.description}
            onChange={(event) => {
              setDraft((current) => ({ ...current, description: event.target.value }))
            }}
            placeholder="Anything worth noting about this programme..."
            disabled={isBusy}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-education-skills-input">Related skills</Label>
          <SkillChipField
            id="artifact-education-skills-input"
            skills={draft.skills}
            profileSkills={candidate.skills}
            disabled={isBusy}
            onAdd={attemptAddRelatedSkill}
            onRemove={removeSkill}
            placeholder="Add a skill developed in this programme…"
            helperText="Pick from your profile skills or create a new one — it will be added to your profile when you save."
          />
        </div>
      </form>

      <ArtifactCanvasInlineError>{validationError || submitError}</ArtifactCanvasInlineError>

      <ArtifactCanvasFooterBar
        leading={
          isEditMode ? (
            <Button
              type="button"
              variant={confirmingDelete ? 'destructive' : 'ghost'}
              onClick={handleDelete}
              disabled={isBusy}
              className={confirmingDelete ? '' : 'text-destructive hover:text-destructive'}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {isDeleting ? 'Deleting...' : confirmingDelete ? 'Confirm delete' : 'Delete'}
            </Button>
          ) : undefined
        }
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={isSaving}
            saveLabel={isEditMode ? 'Save' : 'Add education'}
            savingLabel="Saving..."
            closeDisabled={isBusy}
            saveDisabled={!isDirty || isBusy}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
